import { ArrowUpRight, Ban, CheckCheck, Inbox, Mailbox, Send, Settings } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { AuditTrailList } from '../../../../components/index-page/AuditTrailList';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { RouteDocumentModal } from '../components/RouteDocumentModal';
import { RoutingHistoryPanel } from '../components/RoutingHistoryPanel';
import { RoutingPreviewPanel } from '../components/RoutingPreviewPanel';
import { RoutingStatusPill } from '../components/RoutingStatusPill';
import { inboxApi } from '../routingApi';
import { DISPATCHING_PATH, INBOX_PATH } from '../routingRules';
import { ROUTING_STATUS, type DispatchedDocumentDetail } from '../routingTypes';

type TabKey = 'info' | 'preview' | 'routing' | 'audit';

/**
 * One Inbox item — a dispatch addressed to a unit, read from the unit that receives it.
 *
 * **It owns no record**, so the Details card has no Edit: its fields are the SENDER's, corrected
 * in Dispatching (the header menu links there, under Dispatching's own read key). What this page
 * does is the receiving side's two acts — **Receive** it, then **Route** it on — each on its own
 * Inbox key, from the banner and from the Routing tab.
 *
 * **The named addressee is not shown** — a dispatch to a unit has none; an outside office's
 * dispatch never reaches an Inbox.
 *
 * Tabs as Dispatching's: **Preview** on `document-inbox.download` and READ-ONLY (the enclosures
 * are what the sender sent), **Routing** with the Inbox's verbs, **Audit trail** on
 * `audit-logs.read`. It lands on Info: a desk opens an arrival to see what it is and take it in.
 */
export function InboxProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [document, setDocument] = useState<DispatchedDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [routing, setRouting] = useState(false);
  // ONE piece of state holds which banner write is pending.
  const [confirming, setConfirming] = useState<'receive' | 'acknowledge' | null>(null);
  const [running, setRunning] = useState(false);
  const [receiveError, setReceiveError] = useState<ApiError | null>(null);
  const [panelKey, setPanelKey] = useState(0);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      inboxApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setDocument(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this document.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** A write changed the route — the header, banner and counts move with it. */
  const changed = useCallback(() => {
    setPanelKey((k) => k + 1);
    load();
  }, [load]);

  // The open routing THIS reader may take in — the server says which, per line.
  const mine = document?.pendingRoutes.find((p) => p.canAcknowledge) ?? null;

  async function runConfirmed() {
    if (!document || !confirming) return;

    setRunning(true);
    setReceiveError(null);

    try {
      if (confirming === 'receive') {
        await inboxApi.receive(document.id);
        toast.success(`Received ${document.trackingNumber} at ${document.recipientOrgUnitName ?? 'the unit'}.`);
      } else if (mine) {
        await inboxApi.acknowledge(document.id, mine.id);
        toast.success(`Acknowledged at ${mine.toOrgUnitName ?? 'the unit'}.`);
      }

      setConfirming(null);
      changed();
    } catch (caught) {
      setReceiveError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Document Management',
    'Routing',
    { label: 'Inbox', to: INBOX_PATH },
    document?.trackingNumber ?? 'Document',
  ];

  // The full-page error is for a failure with NOTHING to show; a failed refresh keeps the page.
  if (error && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (!document) return null;

  // The server's per-caller answer — received first, then routed from a unit holding it.
  const blocked = document.canRoute ? null : (document.routeBlockedReason ?? 'It cannot be routed on now.');

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="inbox-profile"
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="inbox-profile-header"
            icon={<Mailbox className="h-6 w-6" aria-hidden="true" />}
            title={document.subject}
            meta={[
              document.trackingNumber,
              document.documentTypeName,
              document.mediumLabel,
              `From ${document.originatingOrgUnitName}`,
              `To ${document.recipientOrgUnitName ?? '—'}`,
            ]}
            status={<RoutingStatusPill status={document.status} label={document.statusLabel} isActive={document.isActive} />}
            createdBy={document.createdByName}
            createdAt={document.createdAt}
            actions={
              <Menu
                id="inbox-profile-cog"
                items={[
                  {
                    key: 'open-dispatch',
                    label: 'Open in Dispatching',
                    icon: <ArrowUpRight className="h-4 w-4" aria-hidden="true" />,
                    permission: 'document-dispatching.read',
                    onClick: () => navigate(`${DISPATCHING_PATH}/${document.id}`),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Document options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={banner()}
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'preview',
            permission: 'document-inbox.download',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Preview <CountBadge count={document.attachmentCount} />
              </span>
            ),
          },
          {
            key: 'routing',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Routing <CountBadge count={document.routeCount} />
              </span>
            ),
          },
          { key: 'audit', label: 'Audit trail', permission: 'audit-logs.read' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            {/* The SENDER's fields — corrected in Dispatching, so no Edit here. */}
            <ProfileCard id="inbox-profile-details" title="Details">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Tracking no.', value: document.trackingNumber, mono: true },
                  { label: 'Subject', value: document.subject },
                  { label: 'Document type', value: document.documentTypeName },
                  { label: 'Sent as', value: document.mediumLabel },
                  { label: 'From unit', value: document.originatingOrgUnitName },
                  { label: 'To (unit)', value: document.recipientOrgUnitName },
                  { label: 'Sent by', value: document.modeLabel },
                  { label: 'Dispatched', value: formatDateTime(document.dispatchedAt), mono: true },
                  { label: 'Waybill / receipt no.', value: document.carrierReferenceNumber, mono: true },
                  {
                    label: 'Received',
                    value: document.inboxReceivedAt != null ? formatDateTime(document.inboxReceivedAt) : null,
                    mono: true,
                  },
                  { label: 'Received by', value: document.inboxReceivedByName },
                  // A SET — several units once it has been routed on to more than one; one line, joined.
                  { label: 'With', value: document.holders.length > 0 ? document.holders.map((h) => h.name).join(', ') : null },
                  { label: 'Remarks', value: document.remarks },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="inbox-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: document.createdByName },
                  { label: 'Created', value: formatDateTime(document.createdAt), mono: true },
                  { label: 'Updated by', value: document.updatedByName },
                  { label: 'Updated', value: document.updatedAt ? formatDateTime(document.updatedAt) : null, mono: true },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'preview' ? (
          <RoutingPreviewPanel
            key={panelKey}
            recordId={document.id}
            trackingNumber={document.trackingNumber}
            api={inboxApi}
            modulePrefix="document-inbox"
            isActive={document.isActive}
            onChanged={() => load()}
          />
        ) : tab === 'routing' ? (
          <div className="flex h-full min-h-0 flex-col p-4">
            <RoutingHistoryPanel
              key={panelKey}
              recordId={document.id}
              api={inboxApi}
              movement={{
                api: inboxApi,
                modulePrefix: 'document-inbox',
                verb: 'Route',
                onRoute: () => setRouting(true),
                routeDisabledReason: blocked,
              }}
              onChanged={changed}
            />
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col p-4">
            <AuditTrailList module="document-inbox" entityId={document.id} emptyMessage="No recorded activity for this document yet." />
          </div>
        )}
      </ProfileHeaderShell>

      {routing ? (
        <RouteDocumentModal
          open
          api={inboxApi}
          document={document}
          onClose={() => setRouting(false)}
          onSaved={() => {
            setRouting(false);
            changed();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setReceiveError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming === 'receive' ? 'Receive document' : 'Acknowledge receipt'}
          requireConfirm
          loading={running}
          id={`inbox-profile-${confirming}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming === 'receive' ? (
                <p>
                  {document.recipientOrgUnitName ?? 'The unit'} takes {document.trackingNumber} in. It is that unit&apos;s
                  from now on, and {document.originatingOrgUnitName} can no longer delete it or change where it was sent.
                </p>
              ) : (
                <p>
                  {mine?.toOrgUnitName ?? 'The unit'} takes {document.trackingNumber} in. It is that
                  unit&apos;s from now on, and the forward becomes part of its history — it can no longer be cancelled.
                </p>
              )}

              {receiveError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {receiveError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );

  /** The banner, one per state — it renders for every state, not only the bad ones. */
  function banner(): ReactNode {
    if (!document) return null;

    if (error) {
      return (
        <ProfileBanner
          id="inbox-profile-banner"
          icon={<Ban className="h-4 w-4" aria-hidden="true" />}
          message={error.message}
        />
      );
    }

    if (!document.isActive) {
      return (
        <ProfileBanner
          id="inbox-profile-banner"
          icon={<Ban className="h-4 w-4" aria-hidden="true" />}
          message={`${document.originatingOrgUnitName} deleted this dispatch. It can no longer be received or routed; its route and files are kept.`}
        />
      );
    }

    const state = stateOf(document);

    return (
      <ProfileBanner
        id="inbox-profile-banner"
        icon={state.icon}
        message={state.message}
        action={
          // Who you ARE decides which act is yours: only the recipient unit receives it.
          document.canReceive ? (
            // WRITES, outside a modal — so it confirms with a tick.
            <PermissionGate permission="document-inbox.receive">
              <Button
                id="inbox-profile-receive"
                variant="primary"
                leading={<CheckCheck className="h-4 w-4" aria-hidden="true" />}
                onClick={() => setConfirming('receive')}
              >
                Receive
              </Button>
            </PermissionGate>
          ) : mine ? (
            // WRITES, outside a modal — so it confirms with a tick. The Routing tab's quick action is the same verb.
            <PermissionGate permission="document-inbox.route">
              <Button
                id="inbox-profile-acknowledge"
                variant="primary"
                leading={<CheckCheck className="h-4 w-4" aria-hidden="true" />}
                onClick={() => setConfirming('acknowledge')}
              >
                Acknowledge
              </Button>
            </PermissionGate>
          ) : document.canRoute ? (
            // Opens a form, so no confirmation of its own — the form's tick is the gate.
            <PermissionGate permission="document-inbox.route">
              <Button
                id="inbox-profile-route"
                variant="primary"
                leading={<Send className="h-4 w-4" aria-hidden="true" />}
                onClick={() => setRouting(true)}
              >
                Route
              </Button>
            </PermissionGate>
          ) : null
        }
      />
    );
  }
}

function stateOf(document: DispatchedDocumentDetail): { icon: ReactNode; message: string } {
  if (document.inboxReceivedAt == null) {
    return {
      icon: <Send className="h-4 w-4" aria-hidden="true" />,
      message: `Waiting — sent by ${document.originatingOrgUnitName} on ${formatDateTime(document.dispatchedAt)}, ${document.modeLabel.toLowerCase()}. It is ${document.originatingOrgUnitName}'s until ${document.recipientOrgUnitName ?? 'this unit'} receives it.`,
    };
  }

  if (document.status === ROUTING_STATUS.inTransit) {
    const to = document.pendingRoutes.map((p) => p.toOrgUnitName ?? 'a unit');
    const holders = document.holders.map((h) => h.name).join(', ');

    // Every open routing named — there may be several, and each unit acknowledges its own.
    return {
      icon: <Send className="h-4 w-4" aria-hidden="true" />,
      message:
        to.length > 0
          ? `On its way to ${to.join(', ')} — not yet acknowledged. With ${holders || 'the unit that sent it'} until then.`
          : 'In transit to another unit, awaiting acknowledgement.',
    };
  }

  return {
    icon: <Inbox className="h-4 w-4" aria-hidden="true" />,
    // Two facts, each its own unit: who holds it NOW, and which Inbox took it in — they part once it moves on.
    message: `With ${document.holders.map((h) => h.name).join(', ')}. ${document.recipientOrgUnitName ?? 'The unit it was sent to'} received it on ${formatDateTime(document.inboxReceivedAt)}${
      document.inboxReceivedByName ? ` (${document.inboxReceivedByName})` : ''
    }. ${document.canRoute ? 'Route it to the next unit when it is done there.' : (document.routeBlockedReason ?? '')}`,
  };
}
